<template>
  <div class="graph-container" ref="chartContainer"></div>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "QpsGraph",
  props: {
    qps: {
      type: Number,
      required: true
    },
    points: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      svg: null,
      margin: { top: 20, right: 30, bottom: 50, left: 60 },
      width: 0,
      height: 0,
      xScale: null,
      yScale: null,
      xAxis: null,
      yAxis: null,
      line: null,
      resizeObserver: null,
      xAxisLabel: null, // Reference to x-axis label
      yAxisLabel: null  // Reference to y-axis label
    };
  },
  mounted() {
    this.initChart();
    this.updateChart();
    this.setupResizeObserver();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  watch: {
    points: {
      handler () {
        this.updateChart();
      },
      deep: true
    },
    qps: {
      handler () {
        this.updateChart();
      },
      deep: true
    }
  },
  methods: {
    initChart() {
      const container = this.$refs.chartContainer;

      // Set dimensions based on the container
      this.width = container.clientWidth - this.margin.left - this.margin.right;
      this.height = container.clientHeight
        ? container.clientHeight - this.margin.top - this.margin.bottom
        : 300; // Fallback height

      // Create the SVG
      this.svg = d3
        .select(container)
        .append("svg")
        .attr("width", container.clientWidth)
        .attr("height", container.clientHeight)
        .append("g")
        .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

      // Initialize scales
      this.xScale = d3.scaleLinear().range([0, this.width]);
      this.yScale = d3.scaleLinear().range([this.height, 0]);

      // Initialize axes
      this.xAxis = this.svg.append("g")
        .attr("class", "x axis")
        .attr("transform", `translate(0,${this.height})`);

      this.yAxis = this.svg.append("g").attr("class", "y axis");

      // Add the line path
      this.svg.append("path")
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", "steelblue")
        .attr("stroke-width", 2);

      // Add x-axis label and store reference
      this.xAxisLabel = this.svg.append("text")
        .attr("class", "x-axis-label")
        .attr("text-anchor", "middle")
        .attr("x", this.width / 2)
        .attr("y", this.height + this.margin.bottom - 10)
        .text("UTC Hour"); // Replace with your desired label text

      // Add y-axis label and store reference
      this.yAxisLabel = this.svg.append("text")
        .attr("class", "y-axis-label")
        .attr("text-anchor", "middle")
        .attr("transform", `rotate(-90)`)
        .attr("x", -this.height / 2)
        .attr("y", -this.margin.left + 15)
        .text("QPS");
    },
    updateChart() {
      const data = this.processData(this.points);

      // Update scales
      this.xScale.domain(d3.extent(data, (d) => d.x));
      this.yScale.domain([0, d3.max(data, (d) => d.y) * 1.1]);

      // Update axes
      this.xAxis.transition().duration(500).call(d3.axisBottom(this.xScale).ticks(12));
      this.yAxis.transition().duration(500).call(d3.axisLeft(this.yScale).ticks(5));

      // Add or update vertical grid lines
      const gridLines = this.svg.selectAll(".vertical-grid-line").data(data);

      // Enter: Add new lines
      gridLines
        .enter()
        .append("line")
        .attr("class", "vertical-grid-line")
        .attr("stroke", "lightgrey")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,2") // Dashed line style
        .merge(gridLines) // Merge enter and update selections
        .transition()
        .duration(500)
        .attr("x1", (d) => this.xScale(d.x))
        .attr("x2", (d) => this.xScale(d.x))
        .attr("y1", 0) // Start at the top of the chart
        .attr("y2", this.height); // Extend to the bottom of the chart

      // Exit: Remove unused lines
      gridLines.exit().remove();

      // Update the line
      this.svg.select(".line")
        .datum(data)
        .transition()
        .duration(500)
        .attr(
          "d",
          d3
            .line()
            .curve(d3.curveCatmullRom.alpha(0.5))
            .x((d) => this.xScale(d.x))
            .y((d) => this.yScale(d.y))
        );
    },
    processData(points) {
      return Object.keys(points)
        .map((hour) => ({
          x: +hour,
          y: points[hour] * this.qps
        }))
        .sort((a, b) => a.x - b.x);
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target === this.$refs.chartContainer) {
            this.resizeChart();
          }
        }
      });

      this.resizeObserver.observe(this.$refs.chartContainer);
    },
    resizeChart() {
      const container = this.$refs.chartContainer;

      // Update dimensions
      const newWidth = container.clientWidth - this.margin.left - this.margin.right;
      const newHeight =
        container.clientHeight > 0
          ? container.clientHeight - this.margin.top - this.margin.bottom
          : 300;

      // Only update if dimensions change
      if (newWidth !== this.width || newHeight !== this.height) {
        this.width = newWidth;
        this.height = newHeight;

        // Resize the SVG
        d3.select(this.$refs.chartContainer).select("svg")
          .attr("width", this.width + this.margin.left + this.margin.right)
          .attr("height", this.height + this.margin.top + this.margin.bottom);

        // Update scales
        this.xScale.range([0, this.width]);
        this.yScale.range([this.height, 0]);

        // Update axes
        this.xAxis.attr("transform", `translate(0,${this.height})`);

        // Re-render the chart
        this.updateChart();

        // **Update the x-axis label position**
        if (this.xAxisLabel) {
          this.xAxisLabel
            .attr("x", this.width / 2)
            .attr("y", this.height + this.margin.bottom - 10);
        }

        // **Update the y-axis label position**
        if (this.yAxisLabel) {
          this.yAxisLabel
            .attr("x", -this.height / 2)
            .attr("y", -this.margin.left + 15);
        }
      }
    }
  }
};
</script>

<style scoped>
.graph-container {
  height: 100%; /* Allow it to match the height of the parent */
  width: 100%; /* Full width of the parent column */
  overflow: hidden; /* Prevent overflow issues */
  display: flex; /* Ensure the container respects flexbox constraints */
}

svg {
  display: block; /* Prevent inline SVG spacing issues */
  width: 100%; /* Make the SVG fill the width of the container */
  height: 100%; /* Make the SVG fill the height of the container */
}
</style>



// WEBPACK FOOTER //
// src/demand/pages/components/QpsGraph.vue